// Post-Acute Withdrawal Syndrome (PAWS) week-by-week expectations for
// 7-OH / kratom taper. Hand-written from r/quittingkratom community
// lore, opioid PAWS clinical literature, and harm-reduction guides.
//
// Each entry covers a stretch of the taper. The lookup helper picks
// the most-relevant entry by week index (rounded down to nearest
// covered week). This is a tracker, not medical advice — every entry
// avoids prescribing or guaranteeing.

export interface PAWSWeek {
  weekIndex: number; // first week this entry applies to
  title: string;
  expected: string[];
  coping: string[];
  reassurance: string;
}

export const PAWS_CALENDAR: PAWSWeek[] = [
  {
    weekIndex: 0,
    title: 'Week 1 — the first cut',
    expected: [
      'Mild restlessness, runny nose, watery eyes the first 24–36 hours.',
      'Sleep gets shallow. Yawning, sneezing fits.',
      'Mood dips below baseline — flatter, not necessarily dark.',
    ],
    coping: [
      'Hydrate. Electrolytes if anything other than water tastes bland.',
      'Plan an early bedtime. Sleep debt makes everything sharper.',
      'Move your body once a day, even just a walk.',
    ],
    reassurance: 'This is the body noticing the change. It is not yet asking for anything dramatic.',
  },
  {
    weekIndex: 2,
    title: 'Week 2 — the body settles in',
    expected: [
      'Sleep often gets worse before it gets better.',
      'Mild aches in the lower back, calves, jaw — a flu-like background.',
      'Gut shifts between constipation and looser stools.',
      'Cravings spike at familiar times of day.',
    ],
    coping: [
      'Magnesium glycinate in the evening helps some people with the muscle aches and sleep.',
      'Hot showers and gentle stretching for the body aches.',
      'Use the urge-surf timer when a craving hits — most last 3–10 minutes.',
    ],
    reassurance: 'Week 2 is where the work feels most real. The schedule is doing its job; you just feel it.',
  },
  {
    weekIndex: 4,
    title: 'Weeks 3–4 — the deepest dip',
    expected: [
      'For many people, this is the hardest stretch. Low mood, low energy.',
      'Anhedonia: things that used to feel nice feel flat.',
      'Sleep is still uneven — fragmented, vivid dreams.',
      'Concentration is patchy.',
    ],
    coping: [
      'Be patient with your output. This is a temporary chemistry, not your new normal.',
      'Sunlight in the morning if you can. Even cloudy daylight helps.',
      'One social touch a day — text, call, in-person. Connection counters the flattening.',
      'If you are noticing dark thoughts that scare you, use the crisis resources. This is not a moral failure; it is the chemistry.',
    ],
    reassurance: 'This week is supposed to feel like this. You are not regressing.',
  },
  {
    weekIndex: 6,
    title: 'Weeks 5–6 — daylight again',
    expected: [
      'Sleep starts consolidating. Most people get a real night somewhere in here.',
      'Cravings are smaller and shorter, but still present.',
      'Mood ticks up. Music and food start tasting like themselves again.',
    ],
    coping: [
      'Keep the structure. Routines are the scaffolding while the chemistry rebuilds.',
      'Notice the small wins out loud — to yourself, to a friend.',
      'Beware the "I am better, so I can ___" thought. That is the brain testing the edge.',
    ],
    reassurance: 'The bottom is behind you. The next stretch is steady, not dramatic.',
  },
  {
    weekIndex: 8,
    title: 'Weeks 7–8 — protect the streak',
    expected: [
      'Most days feel close to baseline.',
      'Occasional sharp cravings, often triggered by people or places.',
      'Energy returns unevenly — fine at 10 a.m., wiped by 4 p.m.',
    ],
    coping: [
      'Plan around the energy curve. Hardest tasks before mid-afternoon if you can.',
      'Catalog your triggers (the trigger chips on the dose log are for this).',
      'A setback now is data, not a verdict. Log it; the streak freeze is there.',
    ],
    reassurance: 'You have outlasted the chemistry. What you are managing now is mostly behavior.',
  },
  {
    weekIndex: 12,
    title: 'Weeks 9–12 — the long tail',
    expected: [
      'Cravings become rare and brief.',
      'Sleep quality is mostly back. Dreams are normal.',
      'Some lingering moments of low mood, especially in the late afternoon.',
    ],
    coping: [
      'Identify what you replaced the substance with. If the answer is "nothing," fill the gap intentionally.',
      'Tell someone — even one person — how far you have come. The brain remembers spoken commitments differently.',
    ],
    reassurance: 'Past 90 days, most studies show the relapse rate drops sharply. You are inside the safe stretch.',
  },
  {
    weekIndex: 16,
    title: 'Beyond — the new normal',
    expected: [
      'You will forget about the substance for stretches at a time.',
      'Anniversaries (1 month, 3 months, 6 months) can resurface old feelings unexpectedly.',
      'Stress, illness, and sleep loss are the three honest re-tests.',
    ],
    coping: [
      'Keep one habit from this taper alive — the morning check-in, a weekly walk, the chat when you need it.',
      'The motivational popups and streaks keep working in the background. Let them.',
    ],
    reassurance: 'You are not "in recovery from" anymore. You are someone who came out the other side.',
  },
];

export function pawsForWeek(weekIndex: number): PAWSWeek {
  // Find the highest entry whose weekIndex is <= the requested one.
  let pick = PAWS_CALENDAR[0];
  for (const entry of PAWS_CALENDAR) {
    if (entry.weekIndex <= weekIndex) pick = entry;
    else break;
  }
  return pick;
}

// PR 1C (Gap 5) — PAWS-aware content stream for Day 30+.
//
// Distilled from PAWS_CALENDAR but framed for the long tail specifically.
// The point: users churn out of streak apps right when PAWS hits its
// stride. The Today screen and the Sunday digest both need a "what's
// normal right now" surface so users don't read flat mood, RLS, or
// anhedonia as failure.
//
// Returns null for dayIndex < 30 — the existing Week 1–4 content
// already covers acute withdrawal and we don't want to duplicate it.
export interface PAWSLens {
  /** Day-range label, e.g. "Day 30–45". */
  rangeLabel: string;
  /** One-line headline, declarative. */
  headline: string;
  /** Up to 3 bullets normalizing the phase. Pulled from PAWS_CALENDAR. */
  expected: string[];
  /** One reassurance sentence — quiet, no exclamation marks. */
  reassurance: string;
  /** Source PAWS_CALENDAR weekIndex this lens maps to. */
  sourceWeekIndex: number;
}

export function pawsLensForDay(dayIndex: number): PAWSLens | null {
  if (dayIndex < 30) return null;
  if (dayIndex < 45) {
    const src = pawsForWeek(4);
    return {
      rangeLabel: 'Day 30–45',
      headline: 'You are in the deepest dip — and it is supposed to feel like this.',
      expected: src.expected.slice(0, 3),
      reassurance: src.reassurance,
      sourceWeekIndex: src.weekIndex,
    };
  }
  if (dayIndex < 90) {
    const src = pawsForWeek(6);
    return {
      rangeLabel: 'Day 45–90',
      headline: 'Daylight again. The bottom is behind you.',
      expected: src.expected.slice(0, 3),
      reassurance: src.reassurance,
      sourceWeekIndex: src.weekIndex,
    };
  }
  if (dayIndex < 180) {
    const src = pawsForWeek(12);
    return {
      rangeLabel: 'Day 90–180',
      headline: 'The long tail — chemistry mostly settled, behaviour is the game now.',
      expected: src.expected.slice(0, 3),
      reassurance: src.reassurance,
      sourceWeekIndex: src.weekIndex,
    };
  }
  const src = pawsForWeek(16);
  return {
    rangeLabel: 'Day 180+',
    headline: 'The new normal — and the three honest re-tests.',
    expected: src.expected.slice(0, 3),
    reassurance: src.reassurance,
    sourceWeekIndex: src.weekIndex,
  };
}
